import type { LiveBillResponse } from '@monorepo/shared/schemas/billing'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Activity, RefreshCw, Target, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  type BillingCycle,
  BillingCycleSelector,
} from '@/features/billing/components/BillingCycleSelector'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { type Customer, customerService } from '../../services/customerService'
import { getPeriodDates } from '../../utils/periodDates'

interface CostTabProps {
  customer: Customer
}

type SignalRow = {
  signal_id: string
  signal_name: string
  signal_kind: 'task' | 'workflow' | 'outcome'
  cost: number
  event_count: number
  agent_name: string
}

export function CostTab({ customer }: CostTabProps) {
  const queryClient = useQueryClient()
  const { currentEnvironment } = useEnvironment()
  const [period, setPeriod] = useState<BillingCycle>('current')

  const { fromDate, toDate } = useMemo(() => getPeriodDates(period), [period])

  const agentUsageQuery = useQuery({
    queryKey: [
      'customer-cost',
      customer.id,
      currentEnvironment?.id,
      fromDate,
      toDate,
      'agents',
    ],
    queryFn: () =>
      customerService.getCustomerAgentUsage(customer.id, fromDate, toDate),
  })

  const liveBillQuery = useQuery<LiveBillResponse>({
    queryKey: [
      'customer-cost',
      customer.id,
      currentEnvironment?.id,
      fromDate,
      toDate,
      'liveBill',
    ],
    queryFn: () => customerService.getLiveBill(customer.id, fromDate, toDate),
  })

  const loading = agentUsageQuery.isLoading || liveBillQuery.isLoading

  const agentData = agentUsageQuery.data?.agents ?? []
  const liveBillData = liveBillQuery.data ?? null

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['customer-cost', customer.id],
    })
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount)

  const totalCost = agentData.reduce((s, a) => s + a.total_cost, 0)
  const totalRevenue = liveBillData?.summary.total_revenue_usd ?? 0
  const revenueByAgent = new Map(
    (liveBillData?.agents ?? []).map(a => [a.agent_id, a.revenue_usd])
  )
  const totalMargin = totalRevenue - totalCost
  const marginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0

  const getSignalKindIcon = (kind: 'task' | 'workflow' | 'outcome') => {
    switch (kind) {
      case 'task':
        return <Zap className='w-4 h-4 text-slate-500' />
      case 'workflow':
        return <Activity className='w-4 h-4 text-slate-500' />
      case 'outcome':
        return <Target className='w-4 h-4 text-slate-500' />
      default:
        return <Activity className='w-4 h-4 text-slate-500' />
    }
  }

  const getSignalKindColor = (kind: 'task' | 'workflow' | 'outcome') => {
    switch (kind) {
      case 'task':
        return 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
      case 'workflow':
        return 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-600'
      case 'outcome':
        return 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600'
    }
  }

  if (loading) {
    return (
      <div className='space-y-6'>
        <div className='flex justify-between items-center'>
          <div className='animate-pulse'>
            <div className='h-8 bg-slate-200 dark:bg-slate-800 rounded w-48 mb-2'></div>
            <div className='h-4 bg-slate-200 dark:bg-slate-800 rounded w-80'></div>
          </div>
          <div className='animate-pulse flex gap-2'>
            <div className='h-10 bg-slate-200 dark:bg-slate-800 rounded w-24'></div>
            <div className='h-10 bg-slate-200 dark:bg-slate-800 rounded w-10'></div>
          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
          {(['cost', 'revenue', 'margin', 'efficiency'] as const).map(k => (
            <Card
              key={k}
              className='bg-transparent border h-24 flex items-center'
            >
              <CardContent className='px-3 py-0 w-full'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-200 dark:bg-slate-800 rounded w-20 mb-1'></div>
                  <div className='h-6 bg-slate-200 dark:bg-slate-800 rounded w-16'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const allSignals: SignalRow[] = agentData.flatMap(agent =>
    agent.signals.map(s => ({
      signal_id: s.signal_id,
      signal_name: s.signal_name,
      signal_kind: s.signal_kind,
      cost: s.cost,
      event_count: s.event_count,
      agent_name: agent.agent_name,
    }))
  )

  const signalColumns: ColumnDef<SignalRow>[] = [
    {
      accessorKey: 'signal_name',
      header: 'SIGNAL',
      cell: ({ row }) => (
        <div className='flex items-center gap-2 font-medium'>
          <div className='w-2 h-2 bg-red-500 rounded-full'></div>
          <div className='flex flex-col'>
            <span className='capitalize'>
              {row.original.signal_name.replace(/_/g, ' ')}
            </span>
            <div className='flex items-center gap-2 mt-1'>
              <Badge
                variant='outline'
                className={`${getSignalKindColor(row.original.signal_kind)} text-xs px-1 py-0`}
              >
                {row.original.signal_kind}
              </Badge>
              <span className='text-xs text-muted-foreground'>
                {row.original.agent_name}
              </span>
            </div>
          </div>
        </div>
      ),
      size: 250,
    },
    {
      accessorKey: 'event_count',
      header: 'EVENTS',
      cell: ({ row }) => (
        <div className='font-medium'>
          {row.original.event_count.toLocaleString()}
        </div>
      ),
      size: 100,
    },
    {
      accessorKey: 'cost',
      header: 'TOTAL COST',
      cell: ({ row }) => (
        <div className='font-medium text-red-600'>
          {formatCurrency(row.original.cost)}
        </div>
      ),
      size: 120,
    },
  ]

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-bold'>Cost Analysis</h2>
          <p className='text-muted-foreground'>
            Detailed cost breakdown and margin analysis by agent and signal
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={handleRefresh}>
            <RefreshCw className='w-4 h-4' />
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <div className='flex justify-start'>
        <BillingCycleSelector
          value={period}
          onValueChange={setPeriod}
          className='w-48'
        />
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Cost</span>
              <span className='text-md font-bold text-red-600'>
                {formatCurrency(totalCost)}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              All providers & infrastructure
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Revenue</span>
              <span className='text-md font-bold text-green-600'>
                {formatCurrency(totalRevenue)}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Generated from usage
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Net Margin</span>
              <span
                className={`text-md font-bold ${totalMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}
              >
                {formatCurrency(totalMargin)}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              {marginPct.toFixed(1)}% average margin
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Cost Efficiency</span>
              <span
                className={`text-md font-bold ${
                  marginPct >= 50
                    ? 'text-green-600'
                    : marginPct >= 20
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}
              >
                {marginPct.toFixed(1)}%
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Avg profit margin
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Agent Cost Breakdown - 3-col grid */}
      <div className='space-y-4'>
        <h3 className='text-lg font-semibold text-slate-600'>
          Agent Cost Breakdown
        </h3>
        {agentData.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No agent activity in the selected period.
          </p>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {agentData.map(agent => {
              const agentRevenue = revenueByAgent.get(agent.agent_id) ?? 0
              const agentMargin = agentRevenue - agent.total_cost
              const agentMarginPct =
                agentRevenue > 0 ? (agentMargin / agentRevenue) * 100 : 0

              return (
                <Card key={agent.agent_id} className='bg-transparent border'>
                  <CardHeader>
                    <div className='flex justify-between items-start'>
                      <div>
                        <CardTitle className='text-lg'>
                          {agent.agent_name}
                        </CardTitle>
                        <CardDescription>
                          Profit margin: {agentMarginPct.toFixed(1)}%
                        </CardDescription>
                      </div>
                      <div className='text-right'>
                        <div className='text-xl font-bold text-red-600'>
                          {formatCurrency(agent.total_cost)}
                        </div>
                        <div className='text-sm text-muted-foreground'>
                          Total Cost
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    {/* Cost, Revenue, Margin row */}
                    <div className='grid grid-cols-3 gap-2'>
                      <div className='text-center p-2 bg-muted/50 rounded-lg'>
                        <div className='text-lg font-bold text-red-600'>
                          {formatCurrency(agent.total_cost)}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          Cost
                        </div>
                      </div>
                      <div className='text-center p-2 bg-muted/50 rounded-lg'>
                        <div className='text-lg font-bold text-green-600'>
                          {formatCurrency(agentRevenue)}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          Revenue
                        </div>
                      </div>
                      <div className='text-center p-2 bg-muted/50 rounded-lg'>
                        <div
                          className={`text-lg font-bold ${agentMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {formatCurrency(agentMargin)}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          Margin
                        </div>
                      </div>
                    </div>

                    {/* Signals */}
                    <div>
                      <h4 className='font-medium mb-2'>Signals</h4>
                      <div className='space-y-1'>
                        {agent.signals.map(signal => (
                          <div
                            key={signal.signal_id}
                            className='flex items-center justify-between p-2 border rounded text-sm'
                          >
                            <div className='flex items-center gap-2'>
                              {getSignalKindIcon(signal.signal_kind)}
                              <div>
                                <span className='font-medium capitalize'>
                                  {signal.signal_name.replace(/_/g, ' ')}
                                </span>
                                <div className='mt-0.5'>
                                  <Badge
                                    variant='outline'
                                    className={`${getSignalKindColor(signal.signal_kind)} text-xs px-1 py-0`}
                                  >
                                    {signal.signal_kind}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <span className='font-medium text-red-600'>
                              {formatCurrency(signal.cost)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      <div className='border-t mt-5 border-black'></div>

      {/* Cost by signal table */}
      <div className='space-y-4'>
        <h3 className='text-lg font-semibold text-slate-600'>Cost by signal</h3>
        <div className='[&_table]:bg-transparent [&_thead]:bg-slate-50 dark:[&_thead]:bg-slate-800 [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
          <EnhancedTable
            data={allSignals}
            columns={signalColumns}
            height='300px'
            className='bg-transparent border-0'
          />
        </div>
      </div>
    </div>
  )
}
